import React from 'react';
import { useLocation } from 'react-router-dom';

export interface ScrollToTopProps {
  /**
   * The element that actually scrolls. The app shell pins the page to the viewport
   * (`height: 100vh; overflow: hidden`) and gives `<main>` the `overflow: auto`, so the window itself
   * never scrolls and `window.scrollTo` is a no-op here - the container has to be reset by hand.
   */
  containerRef: React.RefObject<HTMLElement | null>;
}

/**
 * Puts every navigation back at the top of the page.
 *
 * React Router does not restore or reset scroll on its own: it swaps the route's elements underneath a
 * scroll container that keeps whatever offset the previous page left behind. Opening a farm from halfway
 * down the farms overview - or any long list - therefore lands mid-page on the new one.
 *
 * Keyed on the pathname alone. Search params carry filters, paging and tab state on several screens, and
 * yanking the reader to the top every time they tick a filter is its own bug. A hash means the URL names a
 * target on the page, so the scroll belongs to whoever handles the anchor.
 */
export default function ScrollToTop({ containerRef }: ScrollToTopProps) {
  const { pathname, hash } = useLocation();

  // Before paint, so the new page is never drawn at the old scroll offset first.
  React.useLayoutEffect(() => {
    if (hash) return;

    const container = containerRef.current;
    if (container) {
      container.scrollTop = 0;
      container.scrollLeft = 0;
    }

    // Harmless where the shell scrolls, and the fallback for any screen rendered outside it.
    window.scrollTo(0, 0);
  }, [pathname, hash, containerRef]);

  return null;
}
